import React from 'react';
import { useNavigate } from 'react-router-dom';
import Flag from 'react-world-flags';
import Avatar from '../Avatar';
import { CheckmarkIcon } from '../../assets/icons/CheckmarkIcon';
import { HeartOutlineIcon } from '../../assets/icons/HeartOutlineIcon';
import { useTranslation } from '../../contexts/TranslationContext';
import { TipsterPublic } from '../../types/interfaces';

interface ExpertSearchRowProps {
  tipster: TipsterPublic;
  isFollowing: boolean;
  onToggleFollow: (tipsterId: number, currentlyFollowing: boolean) => void;
  topPlace?: number;
  rankVariant?: 'tag' | 'medal';
  showAvatar?: boolean;
  showCountry?: boolean;
  showVerified?: boolean;
  showTags?: boolean;
  showStreak?: boolean;
  showRoi?: boolean;
  showFollowerCount?: boolean;
  showFollowButton?: boolean;
}

const formatFollowers = (count: number): string => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return count.toString();
};

const StreakDots: React.FC<{ picks: number[]; ariaLabel: string }> = ({ picks, ariaLabel }) => {
  const slots = Array.from({ length: 5 }, (_, i) => picks[i]);
  return (
    <div className="expert-list-row__streak" aria-label={ariaLabel}>
      {slots.map((p, i) => (
        <span
          key={i}
          className={
            p === undefined
              ? 'expert-list-row__streak-dot expert-list-row__streak-dot--empty'
              : p === 1
              ? 'expert-list-row__streak-dot expert-list-row__streak-dot--win'
              : 'expert-list-row__streak-dot expert-list-row__streak-dot--loss'
          }
        />
      ))}
    </div>
  );
};

const ExpertSearchRow: React.FC<ExpertSearchRowProps> = ({
  tipster,
  isFollowing,
  onToggleFollow,
  topPlace,
  rankVariant = 'tag',
  showAvatar = true,
  showCountry = true,
  showVerified = true,
  showTags = true,
  showStreak = true,
  showRoi = false,
  showFollowerCount = true,
  showFollowButton = true,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleViewProfile = () => {
    navigate(`/experts/${tipster.id}`);
  };

  const tags = [tipster.tag_1, tipster.tag_2, tipster.tag_3].filter(Boolean) as string[];
  const lastPicks = tipster.last_picks ?? [];
  const hasStreak = showStreak && lastPicks.length > 0;
  const hasTags = showTags && tags.length > 0;

  const roi = tipster.roi ?? null;
  const roiPos = roi != null && roi >= 0;
  const canShowRoi = showRoi && roi != null;

  const followers = formatFollowers(tipster.followers_count);
  const hasCta = showFollowButton || showFollowerCount;
  const displayName = tipster.full_name ?? t.common.unknown;

  const avatarMatrix = tipster.avatar_matrix ?? null;
  const avatarColor1 = tipster.avatar_config?.color1 ?? 'var(--color-primary)';
  const avatarColor2 = tipster.avatar_config?.color2 ?? 'var(--color-bg-elevated)';

  const profileAria = t.common.viewProfile.replace('{name}', displayName);
  const streakAria = t.common.lastPicks.replace('{count}', String(lastPicks.length));

  const medalModifier =
    topPlace === 1
      ? 'expert-list-row__rank-badge--gold'
      : topPlace === 2
      ? 'expert-list-row__rank-badge--silver'
      : topPlace === 3
      ? 'expert-list-row__rank-badge--bronze'
      : 'expert-list-row__rank-badge--blue';

  return (
    <div className="expert-list-row" onClick={handleViewProfile} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleViewProfile()}>
      {topPlace !== undefined && rankVariant === 'tag' && (
        <>
          <div className="expert-list-row__ghost-rank" aria-hidden>{topPlace}</div>
          <div className="expert-list-row__rank-tag">
            #{topPlace.toString().padStart(2, '0')}
          </div>
        </>
      )}

      {showAvatar && (
        <div className="expert-list-row__avatar-col">
          <button
            type="button"
            className="expert-list-row__avatar-button"
            onClick={handleViewProfile}
            aria-label={profileAria}
          >
            {avatarMatrix ? (
              <Avatar
                matrix={avatarMatrix}
                color1={avatarColor1}
                color2={avatarColor2}
                compact
              />
            ) : (
              <div className="expert-list-row__avatar-placeholder" />
            )}
          </button>
          {topPlace !== undefined && (
            <span
              className={`expert-list-row__rank-badge ${
                rankVariant === 'medal' ? `expert-list-row__rank-badge--medal ${medalModifier}` : ''
              }`}
            >
              {topPlace}
            </span>
          )}
        </div>
      )}

      <div className="expert-list-row__identity">
        <div className="expert-list-row__name-row">
          <button
            type="button"
            className="expert-list-row__name"
            onClick={handleViewProfile}
          >
            {displayName}
          </button>
          {showVerified && tipster.is_verified && (
            <span className="expert-list-row__verified" aria-label={t.common.verified}>
              <CheckmarkIcon />
            </span>
          )}
          {showCountry && tipster.country && (
            <span className="expert-list-row__country-pill">
              <Flag code={tipster.country} />
              <span>{tipster.country}</span>
            </span>
          )}
        </div>

        {(hasTags || hasStreak) && (
          <div className="expert-list-row__sub-row">
            {hasTags && (
              <span className="expert-list-row__tags">{tags.join(' · ')}</span>
            )}
            {hasTags && hasStreak && (
              <span className="expert-list-row__sep" aria-hidden>|</span>
            )}
            {hasStreak && <StreakDots picks={lastPicks} ariaLabel={streakAria} />}
          </div>
        )}
      </div>

      {canShowRoi && (
        <div className="expert-list-row__roi">
          <span className="expert-list-row__roi-label">{t.common.roi}</span>
          <span
            className={`expert-list-row__roi-value ${
              roiPos
                ? 'expert-list-row__roi-value--pos'
                : 'expert-list-row__roi-value--neg'
            }`}
          >
            {roiPos ? '+' : '−'}
            {Math.abs(roi as number).toFixed(1)}
            <span className="expert-list-row__roi-pct">%</span>
          </span>
        </div>
      )}

      {hasCta && (
        <div className="expert-list-row__cta">
          {showFollowButton && (
            <button
              type="button"
              className={`expert-list-row__follow-pill ${
                isFollowing ? 'expert-list-row__follow-pill--following' : ''
              }`}
              onClick={(e) => { e.stopPropagation(); onToggleFollow(tipster.id, isFollowing); }}
              aria-label={isFollowing ? t.common.unfollow : t.common.follow}
            >
              <HeartOutlineIcon
                className="expert-list-row__follow-pill-heart"
                fill={isFollowing ? 'currentColor' : 'none'}
              />
              {showFollowerCount && (
                <span className="expert-list-row__follow-pill-count">{followers}</span>
              )}
            </button>
          )}

          {showFollowerCount && (
            <span className="expert-list-row__followers-count">
              <strong>{followers}</strong> {t.common.followers}
            </span>
          )}
          {showFollowButton && (
            <button
              type="button"
              className={`expert-list-row__follow ${
                isFollowing ? 'expert-list-row__follow--following' : ''
              }`}
              onClick={(e) => { e.stopPropagation(); onToggleFollow(tipster.id, isFollowing); }}
            >
              {isFollowing ? (
                <>
                  <CheckmarkIcon className="expert-list-row__follow-icon" />
                  {t.common.following}
                </>
              ) : (
                <>
                  <span className="expert-list-row__follow-plus" aria-hidden>+</span>
                  {t.common.follow}
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpertSearchRow;
